import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Search, RefreshCw, Mail, Phone, Building2, Globe, Calendar,
  MessageSquare, User, Package, Network,
} from "lucide-react";

export default function AdminDemoRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/demo-requests");
      setItems(data);
    } catch (e) {
      console.error("Failed to load demo requests:", e);
      toast.error("Failed to load demo requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase().trim();
    if (!ql) return items;
    return items.filter((r) =>
      (r.email || "").toLowerCase().includes(ql)
      || (r.company_name || "").toLowerCase().includes(ql)
      || (r.contact_name || "").toLowerCase().includes(ql)
      || (r.role || "").toLowerCase().includes(ql)
    );
  }, [items, q]);

  const stats = useMemo(() => ({
    total: items.length,
    new: items.filter((r) => r.status === "new").length,
    thisWeek: items.filter((r) => {
      if (!r.created_at) return false;
      const d = new Date(r.created_at);
      const diff = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
      return diff <= 7;
    }).length,
  }), [items]);

  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="admin-demo-requests-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag">Admin · Pipeline</div>
          <h1 className="font-display text-4xl md:text-6xl mt-2 leading-none text-[#17324D]">
            Demo Requests<span className="text-[#C62828]">.</span>
          </h1>
          <p className="mt-3 text-slate-600 font-body">Every prospect who requested an early-access demo of LumberLoop USA.</p>
        </div>
        <button onClick={load} className="btn-secondary flex items-center gap-2" data-testid="reload-demo-requests">
          <RefreshCw size={16}/> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        <StatCard label="Total Requests" value={stats.total} tone="navy"/>
        <StatCard label="New / Unhandled" value={stats.new} tone="red"/>
        <StatCard label="Last 7 Days"    value={stats.thisWeek} tone="light"/>
      </div>

      {/* Search */}
      <div className="mt-8 flex items-center gap-3">
        <div className="relative flex-1 max-w-xl">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, company, email, role..."
            className="ll-input pl-10"
            data-testid="demo-requests-search"
          />
        </div>
        <div className="text-sm text-slate-500 font-mono-ll">{filtered.length} of {items.length}</div>
      </div>

      {/* List */}
      <div className="mt-6 space-y-3">
        {loading && <div className="text-slate-500 py-10 text-center">Loading demo requests…</div>}
        {!loading && filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center text-slate-500">
            {items.length === 0 ? "No demo requests yet. New submissions from /contact will appear here." : "No matches for that search."}
          </div>
        )}

        {filtered.map((r) => {
          const isOpen = expanded === r.id;
          return (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white shadow-sm hover:border-slate-300 transition-colors" data-testid={`demo-request-${r.id}`}>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : r.id)}
                className="w-full text-left p-5 flex flex-wrap items-center gap-4"
              >
                <div className="w-11 h-11 rounded-lg bg-[#C62828]/10 text-[#C62828] flex items-center justify-center flex-shrink-0">
                  <Building2 size={20}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-lg text-[#17324D] truncate">{r.company_name || "—"}</span>
                    {r.role && <span className="text-[10px] font-mono-ll tracking-widest uppercase text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{r.role}</span>}
                    {r.status === "new" && <span className="text-[10px] font-mono-ll tracking-widest uppercase text-[#C62828] bg-red-50 px-2 py-0.5 rounded">New</span>}
                  </div>
                  <div className="mt-1 text-sm text-slate-600 flex items-center gap-4 flex-wrap font-body">
                    <span className="flex items-center gap-1.5"><User size={13} className="text-slate-400"/> {r.contact_name || "—"}</span>
                    <span className="flex items-center gap-1.5"><Mail size={13} className="text-slate-400"/> {r.email}</span>
                    {r.phone && <span className="flex items-center gap-1.5"><Phone size={13} className="text-slate-400"/> {r.phone}</span>}
                  </div>
                </div>
                <div className="text-xs text-slate-500 font-mono-ll flex items-center gap-1.5 flex-shrink-0">
                  <Calendar size={12}/> {fmt(r.created_at)}
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-slate-100 p-5 grid md:grid-cols-2 gap-5 bg-slate-50/50">
                  <div>
                    <FieldRow icon={Package}  label="Product Categories" value={r.product_categories}/>
                    <FieldRow icon={Network}  label="Dealer Network Size" value={r.dealer_network_size}/>
                    <FieldRow icon={Globe}    label="Website" value={r.website} link/>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-[10px] font-mono-ll tracking-widest uppercase text-slate-500 mb-2">
                      <MessageSquare size={13}/> Message
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700 font-body whitespace-pre-wrap min-h-[80px]">
                      {r.message || <span className="text-slate-400 italic">No message provided.</span>}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <a
                        href={`mailto:${r.email}?subject=Your LumberLoop USA demo&body=Hi ${r.contact_name || ""},%0D%0A%0D%0AThanks for requesting a demo of LumberLoop USA...`}
                        className="btn-primary !py-2 !px-4 text-[13px]"
                      >
                        <Mail size={14}/> Reply
                      </a>
                      {r.phone && (
                        <a href={`tel:${r.phone}`} className="btn-secondary !py-2 !px-4 text-[13px]">
                          <Phone size={14}/> Call
                        </a>
                      )}
                    </div>
                    <div className="mt-3 text-[10px] font-mono-ll text-slate-400">ID: {r.id}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const bg = tone === "navy" ? "bg-[#17324D] text-white border-[#17324D]"
           : tone === "red"  ? "bg-[#C62828] text-white border-[#C62828]"
           : "bg-white text-[#17324D] border-slate-200";
  return (
    <div className={`rounded-xl border p-5 ${bg}`}>
      <div className={`text-[10px] font-mono-ll tracking-[0.24em] uppercase ${tone === "light" ? "text-slate-500" : "text-white/70"}`}>{label}</div>
      <div className="font-display text-4xl mt-2 leading-none">{value}</div>
    </div>
  );
}

function FieldRow({ icon: Icon, label, value, link }) {
  if (!value) return null;
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2 text-[10px] font-mono-ll tracking-widest uppercase text-slate-500 mb-1">
        <Icon size={12}/> {label}
      </div>
      {link ? (
        <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-sm text-[#C62828] hover:underline font-body break-all">{value}</a>
      ) : (
        <div className="text-sm text-[#17324D] font-body">{value}</div>
      )}
    </div>
  );
}
